import React from 'react';
import {
  NetSuiteBillItem,
  NetSuitePurchaseOrderLine,
  NetSuiteReviewCheck,
  NetSuiteReviewRequired,
} from '../types';
import { LineMapping, LineMappingCheck, formatMoney, lineAmount } from '../utils/netsuite';

interface NetSuiteReviewProps {
  review: NetSuiteReviewRequired;
  /** IDs of the checks the user has ticked. */
  confirmed: string[];
  onToggle: (checkId: string) => void;
  /** Bill lines that still need a purchase order line chosen. */
  mappingChecks: LineMappingCheck[];
  purchaseOrderLines: NetSuitePurchaseOrderLine[];
  mapping: LineMapping;
  onMap: (itemIndex: number, lineNumber: number | null) => void;
  onMapAll: (lineNumber: number) => void;
  items: NetSuiteBillItem[];
  currency: string;
  disabled: boolean;
  /** The bill endpoint's full response, behind a toggle. */
  raw?: unknown;
}

/** "EXCEEDS_PURCHASE_ORDER" -> "Exceeds purchase order" */
function humanizeCode(code: string): string {
  const text = code.toLowerCase().replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const toNumber = (v: unknown) =>
  typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
const toText = (v: unknown) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');
const stringify = (v: unknown) => (typeof v === 'string' ? v : JSON.stringify(v, null, 2));

function vendorLabel(ref?: { entityId?: unknown; name?: unknown; vendorName?: unknown }): string {
  if (!ref) return '';
  return [toText(ref.entityId), toText(ref.name)].filter(Boolean).join(' ') || toText(ref.vendorName);
}

const lineLabel = (line: NetSuitePurchaseOrderLine) =>
  `Line ${line.purchaseOrderLine} · ${line.item || 'unnamed item'}${line.itemId ? ` (ID ${line.itemId})` : ''}`;

/** What ticking the box does, in the user's words. */
function confirmLabel(check: NetSuiteReviewCheck): string {
  if (check.code === 'VENDOR_DIFFERS') {
    const vendor = vendorLabel(check.purchaseOrder);
    return vendor ? `Bill ${vendor} anyway` : "Bill the purchase order's vendor anyway";
  }
  if (check.code === 'EXCEEDS_PURCHASE_ORDER') return 'Bill over the purchase order anyway';
  return 'Accept this difference';
}

/** A visual for the check kinds we know; others rely on their message alone. */
const CheckDetail: React.FC<{ check: NetSuiteReviewCheck; similarity?: number; currency: string }> = ({
  check,
  similarity,
  currency,
}) => {
  if (check.code === 'VENDOR_DIFFERS') {
    const percent = similarity !== undefined && Number.isFinite(similarity) ? Math.round(similarity * 100) : null;
    return (
      <div className="nsr__versus">
        <div className="nsr__side">
          <span className="nsr__side-label">On the invoice</span>
          <span className="nsr__side-value">{vendorLabel(check.invoice) || '—'}</span>
        </div>
        <span className="nsr__versus-sign" aria-hidden="true">
          ≠
        </span>
        <div className="nsr__side nsr__side--po">
          <span className="nsr__side-label">On the purchase order</span>
          <span className="nsr__side-value">{vendorLabel(check.purchaseOrder) || '—'}</span>
        </div>
        {percent !== null && (
          <div className="nsr__similarity">
            <span className="nsr__bar" aria-hidden="true">
              <span style={{ width: `${percent}%` }} />
            </span>
            {percent}% name similarity
          </div>
        )}
      </div>
    );
  }

  if (check.code === 'EXCEEDS_PURCHASE_ORDER') {
    const billed = toNumber(check.invoice?.amount);
    const remaining = toNumber(check.purchaseOrder?.remaining);
    const ordered = toNumber(check.purchaseOrder?.ordered);
    const over = billed - remaining;
    const openShare = billed > 0 && Number.isFinite(remaining) ? Math.min(1, Math.max(0, remaining / billed)) : NaN;
    return (
      <div className="nsr__amounts">
        <dl className="nsr__figures">
          <div>
            <dt>Invoice bills</dt>
            <dd>{formatMoney(billed, currency)}</dd>
          </div>
          <div>
            <dt>Left to bill on the order</dt>
            <dd>{formatMoney(remaining, currency)}</dd>
          </div>
          {Number.isFinite(ordered) && (
            <div>
              <dt>Ordered</dt>
              <dd>{formatMoney(ordered, currency)}</dd>
            </div>
          )}
          {Number.isFinite(over) && over > 0 && (
            <div className="is-over">
              <dt>Over by</dt>
              <dd>{formatMoney(over, currency)}</dd>
            </div>
          )}
        </dl>
        {Number.isFinite(openShare) && (
          <>
            <div className="nsr__capacity" aria-hidden="true">
              <span className="nsr__capacity-fill" style={{ width: `${Math.max(openShare * 100, 1.5)}%` }} />
            </div>
            <p className="nsr__capacity-legend">Green: still open on the order · red: billed beyond it</p>
          </>
        )}
      </div>
    );
  }

  return null;
};

/**
 * PURCHASE_ORDER_LINE_UNCLEAR: NetSuite can't tell which order line each bill
 * line belongs to. The user matches them, taking NetSuite's item id and name
 * from the order line while the invoice's own figures and text stay.
 */
const LineMapper: React.FC<{
  checks: LineMappingCheck[];
  lines: NetSuitePurchaseOrderLine[];
  mapping: LineMapping;
  items: NetSuiteBillItem[];
  currency: string;
  disabled: boolean;
  onMap: (itemIndex: number, lineNumber: number | null) => void;
  onMapAll: (lineNumber: number) => void;
}> = ({ checks, lines, mapping, items, currency, disabled, onMap, onMapAll }) => {
  const matched = checks.filter((check) => mapping[check.itemIndex] !== undefined).length;

  return (
    <section className="nsr__section" aria-labelledby="nsr-map-title">
      <div className="nsr__section-head">
        <h4 className="nsr__section-title" id="nsr-map-title">
          Match the invoice lines to the purchase order
        </h4>
        <span className={`nsr__progress${matched === checks.length ? ' is-done' : ''}`}>
          {matched} of {checks.length} matched
        </span>
      </div>
      <p className="nsr__mapper-note">
        The item id and name come from the order line you choose. Quantity, rate and the invoice's own
        text stay exactly as read.
      </p>

      <div className="nsr__po-lines">
        {lines.map((line) => (
          <div className="nsr__po-line" key={line.purchaseOrderLine}>
            <div className="nsr__po-line-main">
              <span className="nsr__po-line-name">{lineLabel(line)}</span>
              {line.description && <span className="nsr__desc">{line.description}</span>}
            </div>
            <div className="nsr__po-line-side">
              {line.remaining !== undefined && (
                <span className="nsr__num">{formatMoney(toNumber(line.remaining), currency)} open</span>
              )}
              <button
                type="button"
                className="nsr__map-all"
                onClick={() => onMapAll(line.purchaseOrderLine)}
                disabled={disabled}
              >
                Match all {checks.length} to this
              </button>
            </div>
          </div>
        ))}
      </div>

      {lines.length === 0 && (
        <p className="nsr__blocking">
          ✕ NetSuite didn't list the purchase order's lines, so they can't be matched here. Open the full
          response below to see what it sent.
        </p>
      )}

      {lines.length === 1 && (
        <p className="nsr__mapper-auto">
          <span aria-hidden="true">✓</span> The order has a single line, so every invoice line is matched
          to it.
        </p>
      )}

      <div className="nsr__table-wrap">
        <table className="nsr__table">
          <thead>
            <tr>
              <th>#</th>
              <th>Invoice line</th>
              <th className="nsr__num">Amount</th>
              <th>Purchase order line</th>
            </tr>
          </thead>
          <tbody>
            {checks.map((check) => {
              const item = items[check.itemIndex];
              const options = check.candidates.length ? check.candidates : lines;
              const value = mapping[check.itemIndex];
              return (
                <tr key={check.checkId} className={value === undefined ? 'is-unmapped' : undefined}>
                  <td className="nsr__lineno">{check.itemIndex + 1}</td>
                  <td>
                    <span className="nsr__item">
                      {item ? item.itemName || item.description : 'line removed'}
                    </span>
                    {item && item.description && item.description !== item.itemName && (
                      <span className="nsr__desc">{item.description}</span>
                    )}
                  </td>
                  <td className="nsr__num">{item ? formatMoney(lineAmount(item), currency) : '—'}</td>
                  <td>
                    <select
                      className="input nsr__select"
                      value={value === undefined ? '' : String(value)}
                      disabled={disabled}
                      aria-label={`Purchase order line for invoice line ${check.itemIndex + 1}`}
                      onChange={(e) => onMap(check.itemIndex, e.target.value === '' ? null : Number(e.target.value))}
                    >
                      <option value="">— choose —</option>
                      {options.map((line) => (
                        <option key={line.purchaseOrderLine} value={line.purchaseOrderLine}>
                          {lineLabel(line)}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const LineMatches: React.FC<{ review: NetSuiteReviewRequired; currency: string }> = ({ review, currency }) => {
  const lines = review.comparison?.lines || [];
  const notBilled = review.comparison?.notBilled || [];
  if (lines.length === 0 && notBilled.length === 0) return null;

  return (
    <section className="nsr__section" aria-labelledby="nsr-lines-title">
      <h4 className="nsr__section-title" id="nsr-lines-title">
        How the invoice lines matched the order
      </h4>
      {lines.length > 0 && (
        <div className="nsr__table-wrap">
          <table className="nsr__table">
            <thead>
              <tr>
                <th>PO line</th>
                <th>Order item</th>
                <th className="nsr__num">Billed now</th>
                <th>Invoice line</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.purchaseOrderLine}>
                  <td className="nsr__lineno">{line.purchaseOrderLine}</td>
                  <td>
                    <span className="nsr__item">{line.item}</span>
                    {line.description && <span className="nsr__desc">{line.description}</span>}
                  </td>
                  <td className="nsr__num">
                    {formatMoney(toNumber(line.billed?.amount), currency)}
                    {line.billed?.quantity !== undefined && (
                      <span className="nsr__desc">
                        {line.billed.quantity} × {formatMoney(toNumber(line.billed.rate), currency)}
                      </span>
                    )}
                  </td>
                  <td>
                    {(line.invoiceLines || []).map((invoiceLine) => (
                      <span className="nsr__match" key={invoiceLine.index}>
                        <strong>Line {invoiceLine.index + 1}</strong> {invoiceLine.itemName}
                        {invoiceLine.matchedBy && <em> · {invoiceLine.matchedBy}</em>}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {notBilled.length > 0 && (
        <div className="nsr__notbilled">
          <p className="nsr__notbilled-title">Order lines this invoice doesn't bill</p>
          <ul>
            {notBilled.map((line) => (
              <li key={line.purchaseOrderLine}>
                <span>
                  <strong>PO line {line.purchaseOrderLine}</strong> · {line.item}
                  {line.description ? ` — ${line.description}` : ''}
                </span>
                <span className="nsr__num">{formatMoney(toNumber(line.remaining), currency)} open</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

/** Everything NetSuite wants settled before it creates the bill: matches and confirmations. */
const NetSuiteReview: React.FC<NetSuiteReviewProps> = ({
  review,
  confirmed,
  onToggle,
  mappingChecks,
  purchaseOrderLines,
  mapping,
  onMap,
  onMapAll,
  items,
  currency,
  disabled,
  raw,
}) => {
  const po = review.purchaseOrder;
  const mappingIds = new Set(mappingChecks.map((check) => check.checkId));
  // Matching is handled by the mapper below, so those checks are not listed again.
  const otherChecks = review.checks.filter((check) => !mappingIds.has(check.id));
  const orderName = po?.tranId ? `purchase order ${po.tranId}` : 'NetSuite';
  const poFacts: [string, string | undefined][] = [
    ['Purchase order', po?.tranId],
    ['Status', po?.status],
    ['Vendor', vendorLabel(review.comparison?.vendor?.purchaseOrder) || undefined],
    ['Subsidiary ID', po?.subsidiaryId],
    ['Memo', po?.memo],
  ];

  return (
    <div className="nsr">
      <header className="nsr__intro">
        <span className="nsr__intro-icon" aria-hidden="true">
          {mappingChecks.length > 0 && otherChecks.length === 0 ? '🔗' : '⚖️'}
        </span>
        <div>
          <h3 className="nsr__title">
            {mappingChecks.length > 0 && otherChecks.length === 0
              ? 'Match the invoice to the purchase order'
              : 'NetSuite needs your confirmation'}
          </h3>
          <p className="nsr__lead">
            {mappingChecks.length > 0
              ? `${mappingChecks.length === 1 ? 'One invoice line is' : `${mappingChecks.length} invoice lines are`} not on ${orderName}, so each needs pointing at one of its lines. Match them below — one order line can take every invoice line.`
              : `The invoice doesn't agree with ${orderName} in ${
                  otherChecks.length === 1 ? 'one place' : `${otherChecks.length} places`
                }. Confirm to create the bill anyway, or go back and correct it — a misread number is the usual cause.`}
          </p>
        </div>
      </header>

      {po && (
        <dl className="nsr__po">
          {poFacts
            .filter((fact): fact is [string, string] => Boolean(fact[1]))
            .map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
        </dl>
      )}

      {mappingChecks.length > 0 && (
        <LineMapper
          checks={mappingChecks}
          lines={purchaseOrderLines}
          mapping={mapping}
          items={items}
          currency={currency}
          disabled={disabled}
          onMap={onMap}
          onMapAll={onMapAll}
        />
      )}

      {otherChecks.length > 0 && (
        <ol className="nsr__checks">
          {otherChecks.map((check) => {
            const isConfirmed = confirmed.includes(check.id);
            return (
              <li
                key={check.id}
                className={`nsr__check${isConfirmed ? ' is-confirmed' : ''}${
                  check.confirmable ? '' : ' is-blocking'
                }`}
              >
                <div className="nsr__check-head">
                  <span className="nsr__code">{humanizeCode(check.code)}</span>
                  <code className="nsr__check-id">{check.id}</code>
                </div>
                <p className="nsr__message">{check.message}</p>
                <CheckDetail
                  check={check}
                  similarity={review.comparison?.vendor?.similarity}
                  currency={currency}
                />
                {check.confirmable ? (
                  <label className="nsr__confirm">
                    <input
                      type="checkbox"
                      checked={isConfirmed}
                      disabled={disabled}
                      onChange={() => onToggle(check.id)}
                    />
                    <span>{confirmLabel(check)}</span>
                  </label>
                ) : (
                  <p className="nsr__blocking">✕ This can't be confirmed — go back and correct the bill.</p>
                )}
              </li>
            );
          })}
        </ol>
      )}

      <LineMatches review={review} currency={currency} />

      {raw !== undefined && raw !== null && (
        <details className="nsb__raw">
          <summary>Full response from NetSuite</summary>
          <pre>{stringify(raw)}</pre>
        </details>
      )}

      {review.requestId && <p className="nsr__request-id">NetSuite request {review.requestId}</p>}
    </div>
  );
};

export default NetSuiteReview;
